import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AutoAwesome,
  CheckCircle,
  Groups,
  Payment as Payments,
  Refresh,
  RocketLaunch,
  Schedule,
  Shield,
  WorkspacePremium,
  ReceiptLong,
  Restore as History,
  Save,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { brandPalette as palette, getContrastText } from '../../../utils/brandPalette';
import { getSubscriptionWorkspace, purchaseCompanyPlan } from '../../../services/integrations/subscription';
import { auth } from '../../../config/firebase';
import PaymentMethodDialog from '../../payments/PaymentMethodDialog';
import { getManualPaymentOptions, reconcileApprovedCompanySubscriptionReceipt, sendConfirmedOnlineReceipt } from '../../../services/integrations/manualPayment';
import { downloadPaymentReceipt } from '../../../utils/paymentReceipt';
import { updateCompanyExtraSeatConfig } from '../../../services/integrations/backend';
import LoadingSpinner from '../../common/LoadingSpinner';
import { friendlyError } from '../../../utils/userMessages';

const money = (value, currency = 'EUR') => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(value || 0));
  } catch (_) {
    return `${currency} ${Number(value || 0).toFixed(2)}`;
  }
};

const toDate = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (typeof value?._seconds === 'number') return new Date(value._seconds * 1000);
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const Feature = ({ children }) => (
  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
    <CheckCircle sx={{ fontSize: 16, color: palette.success }} />
    <Typography variant="body2" sx={{ color: palette.inkSoft, fontWeight: 650 }}>{children}</Typography>
  </Stack>
);

const SubscriptionSettings = ({ companyId }) => {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState('');
  const [data, setData] = useState(null);
  const [savingSeatPrice, setSavingSeatPrice] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [receiptConfig, setReceiptConfig] = useState(null);
  const [extraSeatConfig, setExtraSeatConfig] = useState({ price: 0, currency: 'EUR', paymentLink: '', instructions: '' });

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [workspace, paymentOptions] = await Promise.all([
        getSubscriptionWorkspace(companyId),
        getManualPaymentOptions().catch(() => null),
      ]);
      setData(workspace);
      if (paymentOptions?.receipt) setReceiptConfig(paymentOptions.receipt);
    } catch (error) {
      console.error(error);
      toast.error(friendlyError(error, 'Unable to load company subscription.'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data) return;
    const subscription = data.subscription || {};
    const seats = data.seats || {};
    setExtraSeatConfig({
      price: Number(seats.extraSeatPrice ?? subscription.extraSeatPrice ?? 0),
      currency: seats.extraSeatCurrency || subscription.extraSeatCurrency || subscription.currency || 'EUR',
      paymentLink: seats.extraSeatPaymentLink || subscription.extraSeatPaymentLink || '',
      instructions: seats.extraSeatPaymentInstructions || subscription.extraSeatPaymentInstructions || '',
    });
  }, [data]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscription_payment') === 'success') {
      toast.success('Payment received. Subscription activation is being confirmed.');
      setTimeout(async () => {
        await load();
        reconcileApprovedCompanySubscriptionReceipt(companyId).catch((error) => console.warn('Receipt email reconciliation skipped:', error?.message || error));
      }, 1000);
    }
  }, [load, companyId]);

  const subscription = data?.subscription || {};
  const seats = data?.seats || {};
  const plans = data?.plans || [];
  const payments = Array.isArray(data?.payments) ? data.payments : [];
  const manualPayments = Array.isArray(data?.manualPayments) ? data.manualPayments : [];
  const subscriptionHistory = Array.isArray(data?.history) ? data.history : [];
  const used = Number(seats.used ?? subscription.usedSeats ?? 0);
  const reserved = Number(seats.reserved ?? subscription.reservedSeats ?? 0);
  const limit = Number(seats.limit ?? subscription.userLimit ?? subscription.maxUsers ?? 0);
  const unlimited = Boolean(seats.unlimited || subscription.unlimitedUsers || limit === 0);
  const committed = used + reserved;
  const seatPercent = unlimited ? Math.min(100, committed ? 24 : 4) : Math.min(100, (committed / Math.max(1, limit)) * 100);
  const expires = toDate(subscription.expiryDate || subscription.expiresAt);

  const currentPlanId = subscription.planId || subscription.packageId;
  const currentPlan = useMemo(() => plans.find((plan) => plan.id === currentPlanId), [plans, currentPlanId]);

  const saveExtraSeatPricing = async () => {
    if (!companyId) return;
    const price = Number(extraSeatConfig.price || 0);
    if (price < 0) { toast.error('Additional user price cannot be negative.'); return; }
    if (extraSeatConfig.paymentLink && !/^https:\/\//i.test(extraSeatConfig.paymentLink.trim())) { toast.error('Payment link must use HTTPS.'); return; }
    setSavingSeatPrice(true);
    try {
      await updateCompanyExtraSeatConfig(companyId, {
        price,
        currency: extraSeatConfig.currency || subscription.currency || 'EUR',
        paymentLink: extraSeatConfig.paymentLink,
        instructions: extraSeatConfig.instructions,
      });
      toast.success('Additional user-seat pricing saved.');
      await load();
    } catch (error) {
      console.error(error);
      toast.error(friendlyError(error, 'Unable to save additional user-seat pricing.'));
    } finally { setSavingSeatPrice(false); }
  };

  const choosePlan = (plan) => {
    if (!companyId || processing) return;
    const planLimit = plan.unlimitedUsers ? 0 : Number(plan.userLimit ?? plan.maxUsers ?? 0);
    if (!plan.unlimitedUsers && planLimit && planLimit < committed) {
      toast.error(`This plan supports ${planLimit} users, but ${committed} seats are already active or reserved.`);
      return;
    }
    if (Number(plan.price || 0) <= 0) {
      setProcessing(plan.id);
      purchaseCompanyPlan({
        companyId,
        planId: plan.id,
        onSuccess: () => toast.success(`${plan.name} activated successfully.`),
      }).then(load).catch((error) => {
        console.error(error);
        toast.error(friendlyError(error, 'Unable to change subscription.'));
      }).finally(() => setProcessing(''));
      return;
    }
    setCheckoutPlan(plan);
  };

  const continueOnlinePayment = async () => {
    const plan = checkoutPlan;
    if (!plan || !companyId) return null;
    setProcessing(plan.id);
    try {
      const result = await purchaseCompanyPlan({
        companyId,
        planId: plan.id,
        onSuccess: () => toast.success(`${plan.name} activated successfully.`),
      });
      if (!result?.redirected) {
        await load();
        if (Number(plan.price || 0) > 0 && auth.currentUser?.email) {
          sendConfirmedOnlineReceipt({
            companyId,
            email: auth.currentUser.email,
            payment: {
              id: result?.paymentId || result?.orderId || `online_subscription_${companyId}_${plan.id}`,
              paymentType: 'subscription',
              packageId: plan.id,
              packageName: plan.name || '',
              billingCycle: plan.billingCycle || '',
              amount: Number(plan.price || 0),
              currency: plan.currency || 'EUR',
              provider: result?.provider || 'online_gateway',
              providerReference: result?.paymentId || result?.orderId || '',
              status: 'approved',
            },
          }).catch((error) => console.warn('Receipt email skipped:', error?.message || error));
        }
        setCheckoutPlan(null);
      }
      return result;
    } catch (error) {
      console.error(error);
      toast.error(friendlyError(error, 'Unable to change subscription.'));
      throw error;
    } finally {
      setProcessing('');
    }
  };

  const manualPaymentSubmitted = async () => {
    setCheckoutPlan(null);
    toast.success('Payment proof submitted. Your current subscription remains unchanged until Super Admin approves it.');
    await load();
  };

  if (!companyId) return <Alert severity="warning">Company context is still loading.</Alert>;
  if (loading) return <LoadingSpinner compact message="Loading subscription workspace…" />;

  return (
    <Stack spacing={2.25}>
      <Box sx={{
        position: 'relative', overflow: 'hidden', borderRadius: '18px', p: { xs: 2.2, md: 2.8 },
        color: getContrastText(palette.rust),
        background: `linear-gradient(135deg, ${palette.rust}, ${palette.pine})`,
        boxShadow: '0 18px 42px rgba(25,57,78,.16)',
        '&::after': { content: '""', position: 'absolute', width: 210, height: 210, borderRadius: '50%', right: -70, top: -80, background: 'rgba(255,255,255,.12)' },
        '&::before': { content: '""', position: 'absolute', width: 135, height: 135, borderRadius: '50%', right: 110, bottom: -90, background: 'rgba(255,255,255,.09)' },
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} sx={[{ justifyContent: 'space-between', gap: 2 }, { position: 'relative', zIndex: 1 }]}>
          <Stack direction="row" spacing={1.6} sx={{ alignItems: 'center' }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '14px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.28)', boxShadow: '0 10px 24px rgba(0,0,0,.12)' }}><WorkspacePremium /></Box>
            <Box>
              <Typography sx={{ fontSize: 12, opacity: .85, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>Company Subscription</Typography>
              <Typography sx={{ fontSize: { xs: 22, md: 27 }, lineHeight: 1.1, fontWeight: 900 }}>{subscription.planName || currentPlan?.name || 'Subscription'}</Typography>
              <Typography sx={{ mt: .45, opacity: .88, fontSize: 13 }}>Seats, renewals and company activation are controlled here.</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip label={String(subscription.status || 'unknown').toUpperCase()} size="small" sx={{ bgcolor: 'rgba(255,255,255,.2)', color: 'inherit', fontWeight: 850, border: '1px solid rgba(255,255,255,.24)' }} />
            <Button onClick={load} startIcon={<Refresh />} size="small" sx={{ bgcolor: 'rgba(255,255,255,.16)', color: 'inherit', border: '1px solid rgba(255,255,255,.24)', fontWeight: 800, '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}>Refresh</Button>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={1.5}>
        {[
          { icon: <Groups />, label: 'Active users', value: used, color: palette.blue },
          { icon: <Schedule />, label: 'Reserved seats', value: reserved, color: palette.purple },
          { icon: <Shield />, label: 'Seat limit', value: unlimited ? 'Unlimited' : limit, color: palette.green },
          { icon: <Payments />, label: 'Renewal', value: expires ? expires.toLocaleDateString() : '—', color: palette.orange },
        ].map((item) => (
          <Grid key={item.label} size={{ xs: 12, sm: 6, lg: 3 }}>
            <Box sx={{ height: '100%', position: 'relative', overflow: 'hidden', p: 1.8, bgcolor: '#fff', borderRadius: '16px', border: `1px solid ${palette.hairline}`, boxShadow: '0 10px 28px rgba(25,57,78,.08)', '&::after': { content: '""', position: 'absolute', width: 76, height: 76, right: -24, bottom: -28, borderRadius: '50%', background: `${item.color}14` } }}>
              <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Box><Typography sx={{ fontSize: 11, color: palette.inkLight, fontWeight: 850, textTransform: 'uppercase', letterSpacing: '.05em' }}>{item.label}</Typography><Typography sx={{ mt: .4, fontSize: 23, fontWeight: 900, color: palette.ink }}>{item.value}</Typography></Box>
                <Box sx={{ width: 40, height: 40, display: 'grid', placeItems: 'center', borderRadius: '12px', color: '#fff', background: `linear-gradient(135deg, ${item.color}, ${item.color}CC)`, boxShadow: `0 9px 20px ${item.color}30` }}>{item.icon}</Box>
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ p: 2, borderRadius: '16px', bgcolor: '#fff', border: `1px solid ${palette.hairline}`, boxShadow: '0 9px 26px rgba(25,57,78,.065)' }}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box><Typography sx={{ fontWeight: 900, color: palette.ink }}>Seat utilization</Typography><Typography sx={{ fontSize: 12.5, color: palette.inkLight }}>{unlimited ? `${committed} seats in use` : `${committed} of ${limit} seats committed · ${Math.max(0, limit - committed)} available`}</Typography></Box>
          <Typography sx={{ fontWeight: 900, color: seatPercent >= 90 ? palette.danger : palette.rust }}>{unlimited ? '∞' : `${Math.round(seatPercent)}%`}</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={seatPercent} sx={{ height: 8, borderRadius: 99, bgcolor: palette.canvasDeep, '& .MuiLinearProgress-bar': { borderRadius: 99, background: seatPercent >= 90 ? palette.danger : `linear-gradient(90deg, ${palette.rust}, ${palette.pine})` } }} />
        {seatPercent >= 80 && !unlimited && <Alert severity={seatPercent >= 100 ? 'error' : 'warning'} sx={{ mt: 1.5, borderRadius: '12px' }}>{seatPercent >= 100 ? (seats.allowExtraSeats ? `Your included ${limit}-user limit is full. Additional users can continue with the company-set paid-seat charge.` : 'Your company has reached its user limit. Upgrade or release a seat to add another user.') : 'Your company is close to its included user limit.'}</Alert>}
      </Box>

      {!unlimited && seats.allowExtraSeats !== false && (
        <Box sx={{ p: 2, borderRadius: '16px', bgcolor: '#fff', border: `1px solid ${palette.hairline}`, boxShadow: '0 9px 26px rgba(25,57,78,.065)', position: 'relative', overflow: 'hidden' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4} sx={{ alignItems: { md: 'flex-start' }, justifyContent: 'space-between', mb: 1.6 }}>
            <Box>
              <Typography sx={{ fontWeight: 900, color: palette.ink }}>Additional user-seat charge</Typography>
              <Typography sx={{ mt: .25, fontSize: 12.5, color: palette.inkLight }}>Your package includes {limit} users. Set the amount a new user must pay when those included seats are already committed.</Typography>
            </Box>
            <Chip label={`Package limit: ${limit}`} size="small" sx={{ fontWeight: 850 }} />
          </Stack>
          <Grid container spacing={1.2}>
            <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth size="small" type="number" label="Price per extra user" value={extraSeatConfig.price} onChange={(e) => setExtraSeatConfig((p) => ({ ...p, price: e.target.value }))} inputProps={{ min: 0, step: '0.01' }} /></Grid>
            <Grid size={{ xs: 12, sm: 3 }}><TextField fullWidth size="small" label="Currency" value={extraSeatConfig.currency} onChange={(e) => setExtraSeatConfig((p) => ({ ...p, currency: e.target.value.toUpperCase().slice(0, 3) }))} /></Grid>
            <Grid size={{ xs: 12, sm: 5 }}><TextField fullWidth size="small" label="Payment link (optional)" placeholder="https://..." value={extraSeatConfig.paymentLink} onChange={(e) => setExtraSeatConfig((p) => ({ ...p, paymentLink: e.target.value }))} /></Grid>
            <Grid size={{ xs: 12 }}><TextField fullWidth multiline minRows={2} size="small" label="Payment instructions" placeholder="UPI, bank transfer or any instructions shown to users above the included seat limit." value={extraSeatConfig.instructions} onChange={(e) => setExtraSeatConfig((p) => ({ ...p, instructions: e.target.value }))} /></Grid>
          </Grid>
          <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: 1.4 }}><Button onClick={saveExtraSeatPricing} disabled={savingSeatPrice} startIcon={<Save />} variant="contained" sx={{ borderRadius: '11px', textTransform: 'none', fontWeight: 850 }}>{savingSeatPrice ? 'Saving…' : 'Save extra-seat pricing'}</Button></Stack>
        </Box>
      )}

      <Box>
        <Stack direction="row" sx={{ alignItems: 'end', justifyContent: 'space-between', mb: 1.4 }}>
          <Box><Typography sx={{ fontSize: 18, fontWeight: 900, color: palette.ink }}>Available plans</Typography><Typography sx={{ fontSize: 12.5, color: palette.inkLight }}>Upgrade, renew or change company capacity. Users inside the included package limit do not pay; over-limit users follow your additional-seat charge when enabled by the package.</Typography></Box>
        </Stack>
        <Grid container spacing={1.5}>
          {plans.map((plan, index) => {
            const active = plan.id === currentPlanId;
            const planLimit = plan.unlimitedUsers ? 0 : Number(plan.userLimit ?? plan.maxUsers ?? 0);
            const incompatible = !plan.unlimitedUsers && planLimit > 0 && planLimit < committed;
            const accent = [palette.blue, palette.purple, palette.green, palette.orange, palette.rose][index % 5];
            return (
              <Grid key={plan.id} size={{ xs: 12, md: 6, xl: 4 }}>
                <Box sx={{ height: '100%', p: 2, position: 'relative', overflow: 'hidden', bgcolor: '#fff', borderRadius: '16px', border: active ? `1.5px solid ${palette.rust}` : `1px solid ${palette.hairline}`, boxShadow: active ? `0 14px 34px ${palette.rust}20` : '0 8px 24px rgba(25,57,78,.07)', '&::after': { content: '""', position: 'absolute', width: 120, height: 120, borderRadius: '50%', right: -50, top: -55, background: `${accent}0D` } }}>
                  <Stack spacing={1.3} sx={{ position: 'relative', zIndex: 1 }}>
                    <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <Box><Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><Box sx={{ width: 36, height: 36, borderRadius: '11px', display: 'grid', placeItems: 'center', color: '#fff', background: `linear-gradient(135deg, ${accent}, ${accent}CC)`, boxShadow: `0 8px 18px ${accent}30` }}><RocketLaunch sx={{ fontSize: 19 }} /></Box><Box><Typography sx={{ fontWeight: 900, color: palette.ink }}>{plan.name}</Typography><Typography sx={{ fontSize: 11.5, color: palette.inkLight }}>{plan.billingCycle || 'monthly'} billing</Typography></Box></Stack></Box>
                      {active && <Chip size="small" label="CURRENT" color="success" sx={{ fontWeight: 850 }} />}
                    </Stack>
                    <Box><Typography sx={{ fontSize: 25, fontWeight: 950, color: palette.ink }}>{Number(plan.price || 0) === 0 ? 'Free' : money(plan.price, plan.currency || 'EUR')}</Typography><Typography sx={{ fontSize: 12.5, color: palette.inkLight }}>{plan.unlimitedUsers ? 'Unlimited users' : `${planLimit || 1} user seats`}</Typography></Box>
                    <Stack spacing={.6}>{(plan.features || []).slice(0, 5).map((feature) => <Feature key={feature}>{feature}</Feature>)}{!(plan.features || []).length && <><Feature>{plan.unlimitedUsers ? 'Unlimited' : planLimit || 1} company users</Feature><Feature>AI introductions & messaging</Feature><Feature>Calendar & meeting workflow</Feature></>}</Stack>
                    <Button fullWidth disabled={active || incompatible || Boolean(processing)} onClick={() => choosePlan(plan)} startIcon={processing === plan.id ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />} sx={{ mt: .6, borderRadius: '11px', py: 1, textTransform: 'none', fontWeight: 850, color: active ? palette.inkLight : '#fff', bgcolor: active ? palette.canvasDeep : accent, boxShadow: active ? 'none' : `0 8px 18px ${accent}26`, '&:hover': { bgcolor: active ? palette.canvasDeep : accent } }}>{active ? 'Current plan' : incompatible ? `Requires ≤ ${planLimit} seats` : processing === plan.id ? 'Preparing checkout…' : Number(plan.price || 0) === 0 ? 'Switch plan' : 'Upgrade / Select'}</Button>
                  </Stack>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Box sx={{ p: 2, position: 'relative', overflow: 'hidden', borderRadius: '16px', bgcolor: '#fff', border: `1px solid ${palette.hairline}`, boxShadow: '0 10px 28px rgba(25,57,78,.07)', '&::after': { content: '""', position: 'absolute', width: 130, height: 130, borderRadius: '50%', right: -54, top: -62, bgcolor: `${palette.purple}0B` } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={[{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 1.5 }, { position: 'relative', zIndex: 1 }]}>
          <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center' }}>
            <Box sx={{ width: 39, height: 39, borderRadius: '11px', display: 'grid', placeItems: 'center', color: '#fff', background: `linear-gradient(135deg,${palette.purple},${palette.blue})`, boxShadow: `0 8px 18px ${palette.purple}28` }}><ReceiptLong sx={{ fontSize: 20 }} /></Box>
            <Box><Typography sx={{ fontWeight: 900, color: palette.ink }}>Billing & subscription history</Typography><Typography sx={{ fontSize: 12, color: palette.inkLight }}>Recent company subscription payments and plan changes.</Typography></Box>
          </Stack>
          <Chip icon={<History />} size="small" label={`${payments.length + manualPayments.length + subscriptionHistory.length} records`} sx={{ fontWeight: 800 }} />
        </Stack>

        {!payments.length && !manualPayments.length && !subscriptionHistory.length ? (
          <Alert severity="info" sx={{ borderRadius: '12px' }}>No subscription billing history yet. New payments and plan changes will appear here.</Alert>
        ) : (
          <Stack spacing={.8} sx={{ position: 'relative', zIndex: 1 }}>
            {[...payments.map((item) => ({ ...item, recordType: 'payment' })), ...manualPayments.map((item) => ({ ...item, recordType: 'manual_payment' })), ...subscriptionHistory.map((item) => ({ ...item, recordType: 'plan' }))]
              .sort((a, b) => (toDate(b.paidAt || b.approvedAt || b.submittedAt || b.createdAt)?.getTime() || 0) - (toDate(a.paidAt || a.approvedAt || a.submittedAt || a.createdAt)?.getTime() || 0))
              .slice(0, 10)
              .map((item, index) => {
                const date = toDate(item.paidAt || item.approvedAt || item.submittedAt || item.createdAt);
                const paid = item.recordType === 'payment' || item.recordType === 'manual_payment';
                const manual = item.recordType === 'manual_payment';
                const paymentStatus = String(item.status || '').toLowerCase();
                const isApproved = ['approved', 'paid', 'completed', 'success', 'succeeded'].includes(paymentStatus);
                return (
                  <Stack key={`${item.recordType}-${item.id || index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={[{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }, { p: 1.2, borderRadius: '12px', border: `1px solid ${palette.hairline}`, bgcolor: paid ? 'rgba(16,185,129,.025)' : 'rgba(var(--primary-rgb),.025)' }]}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                      <Box sx={{ width: 34, height: 34, flex: '0 0 auto', borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: paid ? 'rgba(16,185,129,.10)' : 'rgba(var(--primary-rgb),.09)', color: paid ? palette.success : 'var(--primary-ink)' }}>{paid ? <Payments sx={{ fontSize: 18 }} /> : <WorkspacePremium sx={{ fontSize: 18 }} />}</Box>
                      <Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontSize: 13, fontWeight: 850, color: palette.ink }}>{paid ? `${manual ? (item.paymentMethod === 'upi' ? 'UPI' : 'Bank transfer') : (item.provider || 'Subscription')} payment` : `${item.planName || 'Subscription'} plan change`}</Typography><Typography sx={{ fontSize: 11.5, color: palette.inkLight }}>{date ? date.toLocaleString() : 'Date unavailable'}{item.status ? ` · ${String(item.status).replaceAll('_', ' ')}` : ''}{manual && item.rejectionReason ? ` · ${item.rejectionReason}` : ''}</Typography></Box>
                    </Stack>
                    <Stack direction="row" spacing={.8} sx={{ alignItems: 'center' }}>
                      {Number(item.amount || 0) > 0 && <Typography sx={{ fontSize: 13, fontWeight: 900, color: palette.ink }}>{money(item.amount, item.currency || 'EUR')}</Typography>}
                      {paid && isApproved && <Button size="small" onClick={() => downloadPaymentReceipt(item, receiptConfig || {})} sx={{ minWidth: 0, textTransform: 'none', fontWeight: 800 }}>Receipt</Button>}
                      <Chip size="small" label={manual ? 'OFFLINE' : paid ? 'PAYMENT' : 'PLAN'} sx={{ height: 23, fontSize: 10, fontWeight: 850 }} />
                    </Stack>
                  </Stack>
                );
              })}
          </Stack>
        )}
      </Box>

      <PaymentMethodDialog
        open={Boolean(checkoutPlan)}
        onClose={() => !processing && setCheckoutPlan(null)}
        onOnlinePayment={continueOnlinePayment}
        onManualSubmitted={manualPaymentSubmitted}
        companyId={companyId}
        userId={auth.currentUser?.uid || ''}
        companyName=""
        userName={auth.currentUser?.displayName || ''}
        email={auth.currentUser?.email || ''}
        paymentType={currentPlanId ? 'upgrade' : 'subscription'}
        plan={checkoutPlan}
        currency={checkoutPlan?.currency || subscription.currency || 'EUR'}
        billingCycle={checkoutPlan?.billingCycle || ''}
      />
    </Stack>
  );
};

export default SubscriptionSettings;
